import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Calendar,
  CheckCircle,
  TrendingUp,
  PoundSterling,
  Award,
  ArrowRight,
  AlertCircle,
  Clock,
  Phone,
  Mail,
  FileText,
  Linkedin,
  Filter,
  RefreshCw,
  Loader2,
  Flame,
  Camera,
  Activity,
  Zap,
} from 'lucide-react';
import type {
  Team,
  TeamMember,
  SalesRoute,
  DashboardKPIs,
  PipelineCount,
  TopClient,
  LeaderboardEntry,
  FollowUpItem,
  UpcomingDemo,
  ActivityFeedItem,
  ContactType,
  DashboardFilters,
  HotLead,
  TopPhotographer,
  Signal,
  ConversionAnalytics,
} from '../../types';
import type { InstantlyAnalytics, InstantlyCampaign } from '../../services/instantly';
import {
  getDashboardKPIs,
  getPipelineOverview,
  getTopClientsByRevenue,
  getCommissionLeaderboard,
  getDashboardFollowups,
  getUpcomingDemos,
  getActivityFeed,
} from '../../services/dashboard';
import { getTeamMembers } from '../../services/teams';
import { formatCurrency } from '../../services/billing';
import { CONTACT_STATUSES, CONTACT_TYPES } from '../../constants';
import { getHotLeads, getTopPhotographersByInfluence, getRecentSignals, getConversionAnalytics } from '../../services/intelligence';
import { getInstantlyAnalytics, getInstantlyCampaigns, getInstantlyOrgSettings } from '../../services/instantly';
import { getOutboundStats, type OutboundStats } from '../../services/outbound';
import { LEAD_SEGMENTS } from '../../constants';

interface DashboardPageProps {
  team: Team;
  userId: string;
  navigate: (route: SalesRoute) => void;
  isAdmin?: boolean;
}

interface KPICardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  colour: string;
  onClick?: () => void;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon: Icon, colour, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    className={`bg-slate-900 rounded-xl border border-slate-800 p-5 text-left transition-all ${
      onClick ? 'hover:border-slate-700 hover:bg-slate-800/50 cursor-pointer' : ''
    }`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className={`w-10 h-10 rounded-lg ${colour} flex items-center justify-center`}>
        <Icon size={20} className="text-white" />
      </div>
      {onClick && <ArrowRight size={16} className="text-slate-500" />}
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-sm text-slate-400 mt-1">{title}</p>
    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
  </button>
);

const DashboardPage: React.FC<DashboardPageProps> = ({ team, userId, navigate, isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [pipeline, setPipeline] = useState<PipelineCount[]>([]);
  const [topClients, setTopClients] = useState<TopClient[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [followups, setFollowups] = useState<FollowUpItem[]>([]);
  const [demos, setDemos] = useState<UpcomingDemo[]>([]);
  const [activities, setActivities] = useState<ActivityFeedItem[]>([]);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [hotLeads, setHotLeads] = useState<HotLead[]>([]);
  const [topPhotographers, setTopPhotographers] = useState<TopPhotographer[]>([]);
  const [recentSignals, setRecentSignals] = useState<Signal[]>([]);
  const [conversionAnalytics, setConversionAnalytics] = useState<ConversionAnalytics | null>(null);

  // Instantly integration state
  const [instantlyAnalytics, setInstantlyAnalytics] = useState<InstantlyAnalytics | null>(null);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaign[]>([]);
  const [instantlySelectedCampaign, setInstantlySelectedCampaign] = useState('');
  const [loadingInstantly, setLoadingInstantly] = useState(false);

  const [filters, setFilters] = useState<DashboardFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [outboundStats, setOutboundStats] = useState<OutboundStats | null>(null);

  const fetchData = useCallback(async () => {
    const [
      kpisData,
      pipelineData,
      topClientsData,
      leaderboardData,
      followupsData,
      demosData,
      activitiesData,
      membersData,
      hotLeadsData,
      topPhotographersData,
      recentSignalsData,
      conversionData,
      outboundData,
    ] = await Promise.all([
      getDashboardKPIs(team.id, filters.owner, filters.leadType as ContactType | undefined),
      getPipelineOverview(team.id, filters.owner, filters.leadType as ContactType | undefined),
      getTopClientsByRevenue(team.id, 10),
      getCommissionLeaderboard(team.id, 30),
      getDashboardFollowups(team.id, filters.owner, 20),
      getUpcomingDemos(team.id, 5),
      getActivityFeed(team.id, 15),
      getTeamMembers(team.id),
      getHotLeads(team.id, 5),
      getTopPhotographersByInfluence(team.id, 5),
      getRecentSignals(team.id, 7, 10),
      getConversionAnalytics(team.id, 30),
      getOutboundStats(team.id, { allTeams: isAdmin }),
    ]);

    setKpis(kpisData);
    setPipeline(pipelineData);
    setTopClients(topClientsData);
    setLeaderboard(leaderboardData);
    setFollowups(followupsData);
    setDemos(demosData);
    setActivities(activitiesData);
    setMembers(membersData);
    setHotLeads(hotLeadsData);
    setTopPhotographers(topPhotographersData);
    setRecentSignals(recentSignalsData);
    setConversionAnalytics(conversionData);
    setOutboundStats(outboundData);
  }, [team.id, filters.owner, filters.leadType, isAdmin]);

  const fetchInstantlyData = useCallback(async () => {
    setLoadingInstantly(true);
    const [campaigns, settings] = await Promise.all([
      getInstantlyCampaigns(),
      getInstantlyOrgSettings(team.id),
    ]);
    setInstantlyCampaigns(campaigns);
    
    const campaignId = settings?.default_campaign_id || (campaigns.length > 0 ? campaigns[0].id : '');
    setInstantlySelectedCampaign(campaignId);
    
    if (campaignId) {
      const analytics = await getInstantlyAnalytics(campaignId, 7);
      setInstantlyAnalytics(analytics);
    }
    setLoadingInstantly(false);
  }, [team.id]);

  const handleInstantlyCampaignChange = async (campaignId: string) => {
    setInstantlySelectedCampaign(campaignId);
    if (campaignId) {
      setLoadingInstantly(true);
      const analytics = await getInstantlyAnalytics(campaignId, 7);
      setInstantlyAnalytics(analytics);
      setLoadingInstantly(false);
    } else {
      setInstantlyAnalytics(null);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
    fetchInstantlyData();
  }, [fetchData, fetchInstantlyData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const getMemberEmail = (userId: string) => {
    const member = members.find((m) => m.user_id === userId);
    return member?.email?.split('@')[0] || userId.slice(0, 8);
  };

  const getStatusColour = (status: string) => {
    const found = CONTACT_STATUSES.find((s) => s.value === status);
    return found?.colour || 'bg-slate-500';
  };

  const getStatusLabel = (status: string) => {
    const found = CONTACT_STATUSES.find((s) => s.value === status);
    return found?.label || status;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return Phone;
      case 'email':
        return Mail;
      case 'linkedin':
        return Linkedin;
      default:
        return FileText;
    }
  };

  const totalLeads = pipeline.reduce((sum, p) => sum + p.count, 0);
  const demoCount = pipeline.find((p) => p.status === 'demo_booked' || p.status === 'demo_done');
  const signupCount = pipeline.find((p) => p.status === 'signed_up');
  const leadToDemo = totalLeads > 0 ? ((demoCount?.count || 0) / totalLeads) * 100 : 0;
  const demoToSignup =
    demoCount && demoCount.count > 0 ? ((signupCount?.count || 0) / demoCount.count) * 100 : 0;

  const overdueFollowups = followups.filter((f) => f.is_overdue);
  const todayFollowups = followups.filter((f) => !f.is_overdue);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Sales Dashboard</h1>
          <p className="text-slate-400 text-sm mt-1">Pipeline overview, revenue, and performance</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
              showFilters || filters.owner || filters.leadType
                ? 'bg-orange-500/20 border-orange-500 text-orange-400'
                : 'bg-slate-800 border-slate-700 text-slate-300 hover:text-white'
            }`}
          >
            <Filter size={16} />
            Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-slate-300 hover:text-white transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {outboundStats && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-white">Outbound this week</h2>
            <button onClick={() => navigate('queue')} className="text-xs text-orange-400 hover:text-orange-300">
              Open today’s queue
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <p className="text-2xl font-bold text-white">{outboundStats.sentThisWeek}</p>
              <p className="text-xs text-slate-400">Sent</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{outboundStats.repliedThisWeek}</p>
              <p className="text-xs text-slate-400">Replied</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{outboundStats.draftedUnsentOver3Days}</p>
              <p className="text-xs text-slate-400">Drafted &gt; 3 days</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{outboundStats.byStatus.approved || 0}</p>
              <p className="text-xs text-slate-400">Approved waiting</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-400">
            {Object.entries(outboundStats.byStatus).map(([status, count]) => (
              <span key={status} className="px-2 py-1 bg-slate-800 rounded">
                {status}: {count}
              </span>
            ))}
            {Object.entries(outboundStats.bySegment).map(([segment, count]) => (
              <span key={segment} className="px-2 py-1 bg-slate-800 rounded">
                {LEAD_SEGMENTS.find((s) => s.value === segment)?.label || segment}: {count}
              </span>
            ))}
            {Object.entries(outboundStats.byCountry).map(([country, count]) => (
              <span key={country} className="px-2 py-1 bg-slate-800 rounded">
                {country}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl flex flex-wrap gap-4">
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Sales Owner</label>
            <select
              value={filters.owner || ''}
              onChange={(e) => setFilters({ ...filters, owner: e.target.value || undefined })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All owners</option>
              {members.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {m.email || m.user_id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[150px]">
            <label className="block text-xs font-medium text-slate-400 mb-1.5">Lead Type</label>
            <select
              value={filters.leadType || ''}
              onChange={(e) =>
                setFilters({ ...filters, leadType: (e.target.value as ContactType) || undefined })
              }
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">All types</option>
              {CONTACT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({})}
              className="px-3 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          title="New Leads"
          value={kpis?.new_leads_7d || 0}
          subtitle="Last 7 days"
          icon={Users}
          colour="bg-blue-600"
          onClick={() => navigate('leads')}
        />
        <KPICard
          title="Demos Booked"
          value={kpis?.demos_booked_7d || 0}
          subtitle="Last 7 days"
          icon={Calendar}
          colour="bg-purple-600"
          onClick={() => navigate('deals')}
        />
        <KPICard
          title="Demos Done"
          value={kpis?.demos_completed_7d || 0}
          subtitle="Last 7 days"
          icon={CheckCircle}
          colour="bg-indigo-600"
          onClick={() => navigate('deals')}
        />
        <KPICard
          title="Signups"
          value={kpis?.signups_7d || 0}
          subtitle="Last 7 days"
          icon={TrendingUp}
          colour="bg-emerald-600"
          onClick={() => navigate('leads')}
        />
        <KPICard
          title="Revenue"
          value={formatCurrency(kpis?.revenue_30d_cents || 0, 'gbp')}
          subtitle="Last 30 days"
          icon={PoundSterling}
          colour="bg-amber-600"
          onClick={() => navigate('earnings')}
        />
        <KPICard
          title="Commission"
          value={formatCurrency(kpis?.commission_30d_cents || 0, 'gbp')}
          subtitle="Last 30 days"
          icon={Award}
          colour="bg-pink-600"
          onClick={() => navigate('earnings')}
        />
      </div>

      {/* Pipeline & Conversion */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={18} className="text-orange-500" />
            Pipeline Overview
          </h2>
          <div className="grid grid-cols-7 gap-2 mb-4">
            {CONTACT_STATUSES.map((status) => {
              const count = pipeline.find((p) => p.status === status.value)?.count || 0;
              return (
                <div
                  key={status.value}
                  className="text-center p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className={`w-3 h-3 rounded-full ${status.colour} mx-auto mb-2`} />
                  <div className="text-xl font-bold text-white">{count}</div>
                  <div className="text-xs text-slate-400 truncate">{status.label}</div>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-6 pt-4 border-t border-slate-700">
            <div>
              <span className="text-xs text-slate-400">Lead → Demo</span>
              <div className="text-lg font-semibold text-white">{leadToDemo.toFixed(1)}%</div>
            </div>
            <div>
              <span className="text-xs text-slate-400">Demo → Signup</span>
              <div className="text-lg font-semibold text-emerald-400">{demoToSignup.toFixed(1)}%</div>
            </div>
          </div>
        </div>

        {/* Upcoming Demos */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-orange-500" />
            Upcoming Demos
          </h2>
          {demos.length === 0 ? (
            <p className="text-sm text-slate-400">No upcoming demos scheduled</p>
          ) : (
            <div className="space-y-3">
              {demos.map((demo) => (
                <div
                  key={demo.deal_id}
                  className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                >
                  <div className="text-sm font-medium text-white">{demo.contact_name}</div>
                  {demo.company && <div className="text-xs text-slate-400">{demo.company}</div>}
                  <div className="text-xs text-purple-400 mt-1">
                    {new Date(demo.demo_date).toLocaleDateString('en-GB', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hot Leads & Signals */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Hot Leads */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Flame size={18} className="text-red-500" />
            Hot Leads
            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full ml-2">
              {hotLeads.length}
            </span>
          </h2>
          {hotLeads.length === 0 ? (
            <p className="text-sm text-slate-400">No hot leads (score &gt;= 60)</p>
          ) : (
            <div className="space-y-2">
              {hotLeads.map((lead) => (
                <button
                  key={lead.id}
                  onClick={() => navigate('leads')}
                  className="w-full p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left hover:bg-red-500/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-white">
                      {lead.first_name || lead.last_name
                        ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                        : lead.email || 'Unknown'}
                    </div>
                    <span className="flex items-center gap-1 text-xs font-bold text-red-400">
                      <Flame size={12} />
                      {lead.lead_score}
                    </span>
                  </div>
                  {lead.company && <div className="text-xs text-slate-400">{lead.company}</div>}
                  <div className="text-xs text-slate-500 mt-1 capitalize">{lead.status.replace('_', ' ')}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent Signals */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Zap size={18} className="text-amber-500" />
            Product Signals
            <span className="text-xs text-slate-400 font-normal ml-1">(7 days)</span>
          </h2>
          {recentSignals.length === 0 ? (
            <p className="text-sm text-slate-400">No recent signals</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recentSignals.map((signal) => (
                <div
                  key={signal.id}
                  className="p-3 bg-slate-800/50 border border-slate-700 rounded-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-white">{signal.title}</div>
                    <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">
                      {signal.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {signal.contact_name || 'Unknown'} • {new Date(signal.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Follow-ups & Tasks */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <AlertCircle size={18} className="text-red-500" />
              Overdue Follow-ups
            </h2>
            <span className="text-sm text-red-400">{overdueFollowups.length}</span>
          </div>
          {overdueFollowups.length === 0 ? (
            <p className="text-sm text-slate-400">No overdue items</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {overdueFollowups.slice(0, 8).map((item) => (
                <button
                  key={`${item.source}-${item.id}`}
                  onClick={() => navigate('leads')}
                  className="w-full p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-left hover:bg-red-500/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-white">{item.contact_name}</div>
                    <span className="text-xs text-red-400">
                      {new Date(item.due_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  </div>
                  {item.company && <div className="text-xs text-slate-400">{item.company}</div>}
                  <div className="text-xs text-slate-500 mt-1">{item.title}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Clock size={18} className="text-amber-500" />
              Today's Follow-ups
            </h2>
            <span className="text-sm text-amber-400">{todayFollowups.length}</span>
          </div>
          {todayFollowups.length === 0 ? (
            <p className="text-sm text-slate-400">No follow-ups for today</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {todayFollowups.slice(0, 8).map((item) => (
                <button
                  key={`${item.source}-${item.id}`}
                  onClick={() => navigate('leads')}
                  className="w-full p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-left hover:bg-amber-500/20 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-white">{item.contact_name}</div>
                    <span className="text-xs text-amber-400">
                      {new Date(item.due_at).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  {item.company && <div className="text-xs text-slate-400">{item.company}</div>}
                  <div className="text-xs text-slate-500 mt-1">{item.title}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Revenue & Commission */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Clients by Revenue */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <PoundSterling size={18} className="text-orange-500" />
              Top Clients by Revenue
            </h2>
          </div>
          {topClients.length === 0 ? (
            <div className="p-5 text-sm text-slate-400">No revenue data yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-400">Client</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Revenue</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Credits</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {topClients.map((client) => (
                    <tr key={client.contact_id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-2.5">
                        <div className="text-sm font-medium text-white">{client.contact_name}</div>
                        {client.company && (
                          <div className="text-xs text-slate-400">{client.company}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="text-sm font-medium text-emerald-400">
                          {formatCurrency(client.total_revenue_cents, 'gbp')}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-300">
                        {client.total_credits_purchased}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Commission Leaderboard */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Award size={18} className="text-orange-500" />
              Commission Leaderboard
              <span className="text-xs text-slate-400 font-normal ml-1">(30 days)</span>
            </h2>
          </div>
          {leaderboard.length === 0 ? (
            <div className="p-5 text-sm text-slate-400">No commission data yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-400">
                      Salesperson
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Revenue</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">
                      Commission
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Deals</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {leaderboard.map((entry, index) => (
                    <tr key={entry.sales_user_id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0
                                ? 'bg-amber-500 text-black'
                                : index === 1
                                ? 'bg-slate-400 text-black'
                                : index === 2
                                ? 'bg-amber-700 text-white'
                                : 'bg-slate-700 text-slate-300'
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span className="text-sm font-medium text-white">
                            {getMemberEmail(entry.sales_user_id)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-300">
                        {formatCurrency(entry.total_revenue_cents, 'gbp')}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="text-sm font-medium text-emerald-400">
                          {formatCurrency(entry.total_commission_cents, 'gbp')}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-300">
                        {entry.deals_closed}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top Photographers & Conversion Analytics */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Photographers by Influence */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-800">
            <h2 className="font-semibold text-white flex items-center gap-2">
              <Camera size={18} className="text-orange-500" />
              Top Photographers by Influence
            </h2>
          </div>
          {topPhotographers.length === 0 ? (
            <div className="p-5 text-sm text-slate-400">No photographer data yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-400">Photographer</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Agents</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-400">Revenue Influenced</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {topPhotographers.map((photographer) => (
                    <tr key={photographer.contact_id} className="hover:bg-slate-800/30">
                      <td className="px-4 py-2.5">
                        <div className="text-sm font-medium text-white">{photographer.contact_name}</div>
                        {photographer.company && (
                          <div className="text-xs text-slate-400">{photographer.company}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm text-slate-300">
                        {photographer.agent_count}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="text-sm font-medium text-emerald-400">
                          {formatCurrency(photographer.revenue_influenced_cents, 'gbp')}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Conversion Analytics */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
          <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
            <Activity size={18} className="text-orange-500" />
            Conversion Analytics
            <span className="text-xs text-slate-400 font-normal ml-1">(30 days)</span>
          </h2>
          {conversionAnalytics ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-slate-400">Leads Created</div>
                  <div className="text-xl font-bold text-white">{conversionAnalytics.leads_created}</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-slate-400">Replies</div>
                  <div className="text-xl font-bold text-white">{conversionAnalytics.replies}</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-slate-400">Demos Booked</div>
                  <div className="text-xl font-bold text-purple-400">{conversionAnalytics.demos_booked}</div>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <div className="text-xs text-slate-400">Signups</div>
                  <div className="text-xl font-bold text-emerald-400">{conversionAnalytics.signups}</div>
                </div>
              </div>
              <div className="pt-3 border-t border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Lead → Demo Rate</span>
                  <span className="text-sm font-semibold text-white">{conversionAnalytics.lead_to_demo_rate}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full"
                    style={{ width: `${Math.min(conversionAnalytics.lead_to_demo_rate, 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-slate-400">Demo → Signup Rate</span>
                  <span className="text-sm font-semibold text-emerald-400">{conversionAnalytics.demo_to_signup_rate}%</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-emerald-500 h-2 rounded-full"
                    style={{ width: `${Math.min(conversionAnalytics.demo_to_signup_rate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No conversion data available</p>
          )}
        </div>
      </div>

      {/* Instantly Analytics Widget */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-white flex items-center gap-2">
            <Zap size={18} className="text-purple-500" />
            Instantly
            <span className="text-xs text-slate-400 font-normal ml-1">(7 days)</span>
          </h2>
          {instantlyCampaigns.length > 0 && (
            <select
              value={instantlySelectedCampaign}
              onChange={(e) => handleInstantlyCampaignChange(e.target.value)}
              className="text-sm px-2 py-1 bg-slate-800 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select campaign...</option>
              {instantlyCampaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {loadingInstantly ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
          </div>
        ) : instantlyAnalytics ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-slate-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-white">{instantlyAnalytics.sent}</div>
              <div className="text-xs text-slate-400 mt-1">Sent</div>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-blue-400">{instantlyAnalytics.opens}</div>
              <div className="text-xs text-slate-400 mt-1">Opens ({instantlyAnalytics.open_rate}%)</div>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg text-center">
              <div className="text-2xl font-bold text-emerald-400">{instantlyAnalytics.replies}</div>
              <div className="text-xs text-slate-400 mt-1">Replies ({instantlyAnalytics.reply_rate}%)</div>
            </div>
          </div>
        ) : instantlyCampaigns.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-slate-400 mb-2">No Instantly campaigns found</p>
            <p className="text-xs text-slate-500">Configure Instantly API key and create campaigns</p>
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-4">Select a campaign to view analytics</p>
        )}

        {instantlyAnalytics && instantlyAnalytics.bounces > 0 && (
          <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-center">
            <span className="text-xs text-red-400">{instantlyAnalytics.bounces} bounces detected</span>
          </div>
        )}
      </div>

      {/* Activity Feed */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
        <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
          <FileText size={18} className="text-orange-500" />
          Recent Activity
        </h2>
        {activities.length === 0 ? (
          <p className="text-sm text-slate-400">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {activities.map((activity) => {
              const Icon = getActivityIcon(activity.activity_type);
              return (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg"
                >
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-slate-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-white">{activity.contact_name}</span>
                      <span className="text-xs px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded capitalize">
                        {activity.activity_type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-400 mt-0.5 line-clamp-1">
                      {activity.description}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(activity.happened_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
